// Canonical mapping from a portfolio collection's requirement settings to their
// consequences. This is the single source of truth shared by all four consumers:
//   - the flow add-component picker (web)
//   - the server-side flow-component guard (api)
//   - the setting-disable guard (api)
//   - product activation validation (api)
// Keeping the mapping here prevents those consumers from drifting onto divergent
// ad-hoc setting -> component-type / activation-criterion mappings.
//
// Pure module: no I/O, no dependencies.

import type { SnapshotType } from "./types/flow-instances";

/** The two requirement-setting keys on a portfolio collection. */
export type CollectionRequirementSettingKey = "requiresAssetAllocation" | "requiresTimeseries";

/** A collection's requirement settings — the input to every policy query. */
export type CollectionRequirementSettings = {
  requiresAssetAllocation: boolean;
  requiresTimeseries: boolean;
  /**
   * QP-179 — the collection's sustainability config (shape irrelevant here).
   * Non-null enables the per-portfolio `sustainability` snapshot type.
   */
  sustainabilityConfig?: unknown;
};

/**
 * Flow component types governed by a requirement setting. These string literals
 * are the canonical contract and match the corresponding members of the web
 * `ComponentType` union (apps/web/src/lib/types/flow.ts). Component types not
 * listed here are never gated by these settings.
 */
export const REQUIREMENT_GOVERNED_COMPONENT_TYPES = [
  "asset_allocation",
  "portfolio_forecast",
  "expected_value",
  "historical_return",
  // QL-315 — reads the asset-allocation snapshots for the mix and the forecast
  // snapshots for the weights, so it needs the whole asset-allocation chain.
  "asset_class_forecast",
  // QL-314 — reads the goal's asset_class_forecast snapshot, which is only
  // generated under requiresAssetAllocation. Without this entry the chart was
  // placeable on a collection with the setting off and stayed empty for ever,
  // its compliance gate silently soft-skipping (PR 485 review).
  "volatility_chart",
  // QL-313 — same snapshot, same gating: the equity share chart.
  "equity_share_chart",
  // QL-375 — the mandate check reads its year-1 basis from the same forecast.
  // Governed by THIS setting, not by `mandatesEnabled`: the component must be
  // placeable before mandates are switched on, or a collection with active
  // flows could never enable them (the activation guard refuses until the
  // placement exists). With mandates off it renders nothing and gates nothing.
  "mandate",
  "mandate_comparison",
] as const;

export type RequirementGovernedComponentType =
  (typeof REQUIREMENT_GOVERNED_COMPONENT_TYPES)[number];

/** Identifiers for the product-activation criteria a setting can switch on. */
export type ActivationCriterion = "asset_allocation_sums_to_100" | "has_ticker_and_dataset";

type RequirementPolicyEntry = {
  componentTypes: readonly RequirementGovernedComponentType[];
  activationCriterion: ActivationCriterion;
};

/** The canonical setting -> consequences mapping. */
export const REQUIREMENT_POLICY: Record<CollectionRequirementSettingKey, RequirementPolicyEntry> = {
  requiresAssetAllocation: {
    componentTypes: [
      "asset_allocation",
      "portfolio_forecast",
      "expected_value",
      "asset_class_forecast",
      "volatility_chart",
      "equity_share_chart",
      "mandate",
      "mandate_comparison",
    ],
    activationCriterion: "asset_allocation_sums_to_100",
  },
  requiresTimeseries: {
    componentTypes: ["historical_return"],
    activationCriterion: "has_ticker_and_dataset",
  },
};

/**
 * Goal snapshot types governed by each requirement setting. The unconditional
 * types (`portfolio`, `advisedPortfolio`, `xray`, `cost`) appear in no entry and are
 * always generated where they apply (`advisedPortfolio` for custom portfolios only).
 * `requiresAssetAllocation` governs the asset-allocation chain — asset allocation,
 * forecast, expected value (both the goal-level snapshot and the instance-level
 * aggregate, which share the `expectedValue` refresh type), and the QL-315
 * asset-class forecast (goal-scoped and instance-scoped rows share the
 * `assetClassForecast` refresh type). `requiresTimeseries` governs historical
 * return.
 */
export const REQUIREMENT_GOVERNED_SNAPSHOT_TYPES: Record<
  CollectionRequirementSettingKey,
  readonly SnapshotType[]
> = {
  requiresAssetAllocation: ["assetAllocation", "forecast", "expectedValue", "assetClassForecast"],
  requiresTimeseries: ["historicalReturn"],
};

/** Snapshot types always generated regardless of requirement settings. */
const UNCONDITIONAL_SNAPSHOT_TYPES: readonly SnapshotType[] = [
  "portfolio",
  "advisedPortfolio",
  "xray",
  "cost",
];

/**
 * Goal snapshot types enabled for the given collection settings. Always includes
 * the unconditional types; adds a governed group only while its setting is on.
 * Total over the nullable goal -> flow instance -> flow -> collection relationship:
 * a null/absent collection yields all governed types disabled (only the
 * unconditional four remain).
 */
export function getEnabledSnapshotTypes(
  settings: CollectionRequirementSettings | null | undefined,
): Set<SnapshotType> {
  const enabled = new Set<SnapshotType>(UNCONDITIONAL_SNAPSHOT_TYPES);
  for (const key of Object.keys(
    REQUIREMENT_GOVERNED_SNAPSHOT_TYPES,
  ) as CollectionRequirementSettingKey[]) {
    if (settings?.[key]) {
      for (const type of REQUIREMENT_GOVERNED_SNAPSHOT_TYPES[key]) {
        enabled.add(type);
      }
    }
  }
  // QP-179 — sustainability matching is governed by the presence of the
  // collection's sustainabilityConfig, not by a boolean setting.
  if (settings?.sustainabilityConfig != null) {
    enabled.add("sustainability");
  }
  return enabled;
}

/** The setting key that governs a component type, or `null` if it is ungoverned. */
export function getSettingForComponentType(
  componentType: string,
): CollectionRequirementSettingKey | null {
  for (const key of Object.keys(REQUIREMENT_POLICY) as CollectionRequirementSettingKey[]) {
    if (REQUIREMENT_POLICY[key].componentTypes.includes(componentType as never)) {
      return key;
    }
  }
  return null;
}

/** Governed component types whose owning setting is currently on. */
export function getAllowedComponentTypes(
  settings: CollectionRequirementSettings,
): RequirementGovernedComponentType[] {
  return (Object.keys(REQUIREMENT_POLICY) as CollectionRequirementSettingKey[])
    .filter((key) => settings[key])
    .flatMap((key) => [...REQUIREMENT_POLICY[key].componentTypes]);
}

/** Governed component types whose owning setting is currently off. */
export function getDisallowedComponentTypes(
  settings: CollectionRequirementSettings,
): RequirementGovernedComponentType[] {
  return (Object.keys(REQUIREMENT_POLICY) as CollectionRequirementSettingKey[])
    .filter((key) => !settings[key])
    .flatMap((key) => [...REQUIREMENT_POLICY[key].componentTypes]);
}

/**
 * Whether a component type may be used given the settings. Ungoverned component
 * types are always allowed; a governed type is allowed only while its setting is on.
 */
export function isComponentTypeAllowed(
  settings: CollectionRequirementSettings,
  componentType: string,
): boolean {
  const key = getSettingForComponentType(componentType);
  return key === null ? true : settings[key];
}

/** The activation criteria currently active for the settings (those switched on). */
export function getActiveActivationCriteria(
  settings: CollectionRequirementSettings,
): ActivationCriterion[] {
  return (Object.keys(REQUIREMENT_POLICY) as CollectionRequirementSettingKey[])
    .filter((key) => settings[key])
    .map((key) => REQUIREMENT_POLICY[key].activationCriterion);
}
